import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaIdCard,
    FaWhatsapp,
    FaPassport,
    FaGlobeAmericas,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBuilding,
    FaFlag,
    FaCheckCircle,
    FaArrowRight,
    FaShieldAlt,
    FaLandmark,
} from "react-icons/fa";
import styles from "./RegistrationPage.module.css";

/* =========================================================
   BADGE ICONS — real SVG marks instead of emoji flags
   ========================================================= */

const IndiaFlagIcon = () => (
    <svg width="46" height="46" viewBox="0 0 46 46" role="img" aria-label="India">
        <circle cx="23" cy="23" r="23" fill="#ffffff" />
        <path d="M0 23a23 23 0 0 1 46 0z" fill="#FF9933" transform="translate(0 -7.7)" />
        <rect x="0" y="15.3" width="46" height="15.4" fill="#ffffff" />
        <path d="M0 23a23 23 0 0 0 46 0z" fill="#138808" transform="translate(0 7.7)" />
        <circle cx="23" cy="23" r="5.2" fill="none" stroke="#0B3D91" strokeWidth="0.7" />
        <circle cx="23" cy="23" r="0.9" fill="#0B3D91" />
        {Array.from({ length: 24 }).map((_, i) => (
            <line
                key={i}
                x1="23"
                y1="23"
                x2={23 + 5.2 * Math.cos((i * 15 * Math.PI) / 180)}
                y2={23 + 5.2 * Math.sin((i * 15 * Math.PI) / 180)}
                stroke="#0B3D91"
                strokeWidth="0.45"
            />
        ))}
    </svg>
);

const GlobeBadgeIcon = () => (
    <svg width="46" height="46" viewBox="0 0 46 46" role="img" aria-label="International">
        <circle cx="23" cy="23" r="23" fill="#1370db" />
        <g stroke="#ffffff" strokeWidth="0.9" fill="none" opacity="0.85">
            <ellipse cx="23" cy="23" rx="16" ry="16" />
            <ellipse cx="23" cy="23" rx="6.2" ry="16" />
            <ellipse cx="23" cy="23" rx="11.5" ry="16" />
            <line x1="7" y1="23" x2="39" y2="23" />
            <line x1="9.3" y1="15.5" x2="36.7" y2="15.5" />
            <line x1="9.3" y1="30.5" x2="36.7" y2="30.5" />
        </g>
    </svg>
);

/* =========================================================
   STATIC DATA
   ========================================================= */

const COUNTRY_CODES = [
    { code: "+1", name: "USA / Canada" },
    { code: "+44", name: "United Kingdom" },
    { code: "+61", name: "Australia" },
    { code: "+971", name: "UAE" },
    { code: "+65", name: "Singapore" },
    { code: "+49", name: "Germany" },
    { code: "+33", name: "France" },
    { code: "+81", name: "Japan" },
    { code: "+86", name: "China" },
    { code: "+7", name: "Russia" },
    { code: "+234", name: "Nigeria" },
    { code: "+27", name: "South Africa" },
    { code: "+94", name: "Sri Lanka" },
    { code: "+977", name: "Nepal" },
    { code: "+880", name: "Bangladesh" },
];

const pricingData = [
    { id: "minicourse", tier: "Mini-Course Only", intFee: "$ 300.00", indFee: "INR 5,000.00" },
    { id: "fullaccess", tier: "Full Access Package", intFee: "$ 1,050.00", indFee: "INR 29,500.00", badge: "Most popular" },
    { id: "retiree", tier: "Full Access Retiree Package", intFee: "$ 500.00", indFee: "INR 10,000.00" },
    { id: "student", tier: "Student Full Access, Excluding Mini-Course", intFee: "$ 250.00", indFee: "INR 5,000.00", badge: "Best value" },
    { id: "singleday", tier: "Single-Day Entry Pass", intFee: "$ 425.00", indFee: "N/A" },
];

/* =========================================================
   VALIDATION SCHEMA
   ========================================================= */

const validationSchema = Yup.object().shape({
    delegateType: Yup.string()
        .oneOf(["indian", "foreign"], "Please select a delegate type")
        .required("Please select a delegate type"),

    name: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) =>
            schema
                .trim()
                .min(3, "Name must be at least 3 characters")
                .max(100, "Name is too long")
                .matches(/^[a-zA-Z\u0900-\u097F\s.'-]*$/, "Name should contain valid characters only")
                .required("Full name is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    aadhaarName: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) =>
            schema.trim().min(3, "Name must be at least 3 characters").max(100, "Name is too long").required("Name as per Aadhaar is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    email: Yup.string().trim().email("Please enter a valid email address").required("Email address is required"),

    countryCode: Yup.string().required("Country code is required"),

    mobile: Yup.string()
        .required("Mobile number is required")
        .when("delegateType", {
            is: "indian",
            then: (schema) => schema.matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number"),
            otherwise: (schema) => schema.matches(/^\d{6,15}$/, "Enter a valid mobile number (6-15 digits)"),
        }),

    whatsapp: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) => schema.nullable().matches(/^$|^[6-9]\d{9}$/, "Enter a valid 10-digit WhatsApp number"),
        otherwise: (schema) => schema.notRequired(),
    }),

    aadhaarNo: Yup.string().when("delegateType", {
        is: "indian",
        then: (schema) => schema.required("Aadhaar number is required").matches(/^\d{12}$/, "Aadhaar number must contain exactly 12 digits"),
        otherwise: (schema) => schema.notRequired(),
    }),

    passportName: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().min(3, "Name must be at least 3 characters").required("Name as per passport is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    passportNo: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().min(5, "Enter a valid passport number").max(20, "Passport number is too long").required("Passport number is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    placeOfIssue: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().min(2, "Place of issue is required").required("Place of issue is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    dateOfIssue: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.required("Date of issue is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    nationality: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().required("Nationality is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    affiliation: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().required("Affiliation is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    countryOfBirth: Yup.string().when("delegateType", {
        is: "foreign",
        then: (schema) => schema.trim().required("Country of birth is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
});

/* =========================================================
   SHARED FIELD COMPONENTS
   ========================================================= */

const Field = ({ label, name, type = "text", placeholder, icon, formik, required = false, maxLength, inputMode }) => {
    const hasError = formik.touched[name] && formik.errors[name];

    return (
        <div className={styles.fieldGroup}>
            <label htmlFor={name} className={styles.fieldLabel}>
                <span>
                    {label}
                    {required && <span className={styles.required}> *</span>}
                </span>
            </label>

            <div className={`${styles.inputWrap} ${hasError ? styles.inputError : ""}`}>
                <span className={styles.inputIcon}>{icon}</span>
                <input
                    id={name}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    maxLength={maxLength}
                    inputMode={inputMode}
                    className={styles.styledInput}
                />
            </div>

            {hasError && <div className={styles.errorText}>{formik.errors[name]}</div>}
        </div>
    );
};

const SelectField = ({ label, name, options, formik, icon, required = false }) => {
    const hasError = formik.touched[name] && formik.errors[name];

    return (
        <div className={styles.fieldGroup}>
            <label htmlFor={name} className={styles.fieldLabel}>
                {label}
                {required && <span className={styles.required}> *</span>}
            </label>

            <div className={`${styles.inputWrap} ${hasError ? styles.inputError : ""}`}>
                <span className={styles.inputIcon}>{icon}</span>
                <select
                    id={name}
                    name={name}
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={styles.styledSelect}
                >
                    <option value="">Select {label}</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {hasError && <div className={styles.errorText}>{formik.errors[name]}</div>}
        </div>
    );
};

/* =========================================================
   PAGE
   ========================================================= */

const initialValues = {
    delegateType: "",
    name: "",
    aadhaarName: "",
    aadhaarNo: "",
    email: "",
    countryCode: "+91",
    mobile: "",
    whatsapp: "",
    passportName: "",
    passportNo: "",
    placeOfIssue: "",
    dateOfIssue: "",
    nationality: "",
    affiliation: "",
    countryOfBirth: "",
};

const RegistrationPage = () => {

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            try {
                const displayName = values.delegateType === "foreign" ? values.passportName : values.name;

                console.log("Registration Data:", values);

                await Swal.fire({
                    icon: "success",
                    title: "Registration submitted",
                    html: `
                        <div style="line-height:1.7">
                            <p style="margin-bottom:8px">Thank you, <strong>${displayName}</strong>.</p>
                            <p style="color:#6b6f7d;margin:0">Your registration details have been received successfully.</p>
                        </div>
                    `,
                    confirmButtonText: "Continue",
                    confirmButtonColor: "#096fce",
                });

                resetForm();
            } catch (error) {
                await Swal.fire({
                    icon: "error",
                    title: "Something went wrong",
                    text: "Unable to submit registration details. Please try again.",
                    confirmButtonColor: "#3835cc",
                });
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleDelegateChange = (type) => {
        formik.setValues({
            ...initialValues,
            delegateType: type,
            countryCode: type === "indian" ? "+91" : "+1",
        });
        formik.setTouched({});
    };

    const isIndian = formik.values.delegateType === "indian";
    const isForeign = formik.values.delegateType === "foreign";
    const mobileMaxLength = isIndian ? 10 : 15;

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.backgroundGlowOne}></div>
            <div className={styles.backgroundGlowTwo}></div>

            <div className="container py-2 py-lg-3">

                {/* ================= HEADER ================= */}
                <div className={styles.pageHeader}>
                    <div className={styles.headerBadge}>
                        <FaLandmark />
                        Registration is open
                    </div>

                    {/* <h1 className={styles.pageTitle}>Register for the Summit</h1> */}

                    {/* <p className={styles.pageSubtitle}>
                        Tell us a little about yourself below. We'll ask for a few different
                        details depending on whether you're joining us from India or abroad.
                    </p> */}
                </div>

                {/* ================= DELEGATE TYPE ================= */}
                <div className={styles.delegateSection}>
                    <div className={styles.sectionHeading}>
                        <div className={styles.sectionIcon}>
                            <FaGlobeAmericas />
                        </div>
                        <div>
                            <h3>Where are you joining from?</h3>
                            <p>This decides which details we'll ask for next</p>
                        </div>
                    </div>

                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <button
                                type="button"
                                className={`${styles.delegateCard} ${isIndian ? styles.delegateActive : ""}`}
                                onClick={() => handleDelegateChange("indian")}
                            >
                                <div className={styles.delegateFlag}>
                                    <IndiaFlagIcon />
                                </div>
                                <div className={styles.delegateContent}>
                                    <span className={styles.delegateTitle}>Indian delegate</span>
                                    <span className={styles.delegateDescription}>Registration with Aadhaar details</span>
                                </div>
                                {isIndian && (
                                    <div className={styles.selectedIcon}>
                                        <FaCheckCircle />
                                    </div>
                                )}
                                <FaArrowRight className={styles.delegateArrow} />
                            </button>
                        </div>

                        <div className="col-12 col-md-6">
                            <button
                                type="button"
                                className={`${styles.delegateCard} ${isForeign ? styles.delegateActiveForeign : ""}`}
                                onClick={() => handleDelegateChange("foreign")}
                            >
                                <div className={styles.delegateFlag}>
                                    <GlobeBadgeIcon />
                                </div>
                                <div className={styles.delegateContent}>
                                    <span className={styles.delegateTitle}>Foreign delegate</span>
                                    <span className={styles.delegateDescription}>Registration with passport details</span>
                                </div>
                                {isForeign && (
                                    <div className={styles.selectedIcon}>
                                        <FaCheckCircle />
                                    </div>
                                )}
                                <FaArrowRight className={styles.delegateArrow} />
                            </button>
                        </div>
                    </div>

                    {formik.touched.delegateType && formik.errors.delegateType && (
                        <div className={styles.errorText}>{formik.errors.delegateType}</div>
                    )}
                </div>

                <div className="row g-4">

                    {/* ================= FORM ================= */}
                    <div className="col-12 col-xl-8">
                        <div className={styles.formCard}>
                            <div className={styles.cardHeader}>
                                <div className={styles.cardHeaderIcon}>
                                    <FaUser />
                                </div>
                                <div>
                                    <h2>{isForeign ? "Passport & personal details" : "Personal & contact details"}</h2>
                                    <p>Enter these exactly as they appear on your official documents.</p>
                                </div>
                            </div>

                            <form className="text-start" onSubmit={formik.handleSubmit} noValidate>

                                {/* ================= BASIC INFO ================= */}
                                <div className={styles.formSection}>
                                    <div className={styles.formSectionTitle}>
                                        <span>1</span>
                                        Basic information
                                    </div>

                                    <div className="row g-3">
                                        {isForeign ? (
                                            <div className="col-12">
                                                <Field
                                                    label="Name as per passport"
                                                    name="passportName"
                                                    placeholder="Enter name exactly as shown in passport"
                                                    icon={<FaPassport />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div className="col-12 col-md-6">
                                                    <Field
                                                        label="Full name"
                                                        name="name"
                                                        placeholder="Enter your full name"
                                                        icon={<FaUser />}
                                                        formik={formik}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <Field
                                                        label="Name as per Aadhaar"
                                                        name="aadhaarName"
                                                        placeholder="Enter name as on Aadhaar"
                                                        icon={<FaIdCard />}
                                                        formik={formik}
                                                        required
                                                    />
                                                </div>
                                            </>
                                        )}

                                        <div className="col-12 col-md-6">
                                            <Field
                                                label="Email address"
                                                name="email"
                                                type="email"
                                                placeholder="name@example.com"
                                                icon={<FaEnvelope />}
                                                formik={formik}
                                                required
                                            />
                                        </div>

                                        {isIndian && (
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Aadhaar number"
                                                    name="aadhaarNo"
                                                    placeholder="12-digit Aadhaar number"
                                                    icon={<FaIdCard />}
                                                    formik={formik}
                                                    required
                                                    maxLength={12}
                                                    inputMode="numeric"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* ================= CONTACT ================= */}
                                <div className={styles.formSection}>
                                    <div className={styles.formSectionTitle}>
                                        <span>2</span>
                                        Contact information
                                    </div>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-4">
                                            <div className={styles.fieldGroup}>
                                                <label htmlFor="countryCode" className={styles.fieldLabel}>
                                                    Country code
                                                    <span className={styles.required}> *</span>
                                                </label>

                                                <div className={styles.inputWrap}>
                                                    <span className={styles.inputIcon}>
                                                        <FaGlobeAmericas />
                                                    </span>
                                                    <select
                                                        id="countryCode"
                                                        name="countryCode"
                                                        value={formik.values.countryCode}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        disabled={isIndian}
                                                        className={styles.styledSelect}
                                                    >
                                                        {isIndian ? (
                                                            <option value="+91">+91 India</option>
                                                        ) : (
                                                            COUNTRY_CODES.map((country) => (
                                                                <option key={country.code} value={country.code}>
                                                                    {country.code} {country.name}
                                                                </option>
                                                            ))
                                                        )}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-8">
                                            <Field
                                                label="Mobile number"
                                                name="mobile"
                                                placeholder={isIndian ? "10-digit mobile number" : "Enter mobile number"}
                                                icon={<FaPhone />}
                                                formik={formik}
                                                required
                                                inputMode="numeric"
                                                maxLength={mobileMaxLength}
                                            />
                                        </div>

                                        {isIndian && (
                                            <div className="col-12">
                                                <Field
                                                    label="WhatsApp number"
                                                    name="whatsapp"
                                                    placeholder="Enter WhatsApp number if available"
                                                    icon={<FaWhatsapp />}
                                                    formik={formik}
                                                    maxLength={10}
                                                    inputMode="numeric"
                                                />
                                                <div className={styles.helperText}>WhatsApp number is optional</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* ================= FOREIGN DETAILS ================= */}
                                {isForeign && (
                                    <div className={styles.formSection}>
                                        <div className={styles.formSectionTitle}>
                                            <span>3</span>
                                            Passport information
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Passport number"
                                                    name="passportNo"
                                                    placeholder="Enter passport number"
                                                    icon={<FaPassport />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Place of issue"
                                                    name="placeOfIssue"
                                                    placeholder="City / country"
                                                    icon={<FaMapMarkerAlt />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Date of issue"
                                                    name="dateOfIssue"
                                                    type="date"
                                                    icon={<FaCalendarAlt />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Nationality"
                                                    name="nationality"
                                                    placeholder="Enter nationality"
                                                    icon={<FaFlag />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Affiliation"
                                                    name="affiliation"
                                                    placeholder="Organization / institution"
                                                    icon={<FaBuilding />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <Field
                                                    label="Country of birth"
                                                    name="countryOfBirth"
                                                    placeholder="Enter country of birth"
                                                    icon={<FaGlobeAmericas />}
                                                    formik={formik}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ================= SUBMIT ================= */}
                                <div className={styles.submitArea}>
                                    <div className={styles.securityNote}>
                                        <FaShieldAlt />
                                        <span>Your information is handled securely and used for registration purposes only.</span>
                                    </div>

                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={formik.isSubmitting || !formik.values.delegateType}
                                    >
                                        <span>{formik.isSubmitting ? "Submitting..." : "Continue registration"}</span>
                                        {!formik.isSubmitting && <FaArrowRight />}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* ================= PRICING ================= */}
                    <div className="col-12 col-xl-4">
                        <div className={styles.pricingCard}>
                            <div className={styles.pricingHeader}>
                                <div className={styles.priceIcon}>₹</div>
                                <div>
                                    <h2>Registration fees</h2>
                                    <p>Choose the package that suits your participation.</p>
                                </div>
                            </div>

                            <div className={styles.priceLegend}>
                                <div className={isIndian ? styles.priceTabIndianActive : styles.priceTabIndian}>
                                    <FaFlag /> Indian
                                </div>
                                <div className={isForeign ? styles.priceTabActive : styles.priceTab}>
                                    <FaGlobeAmericas /> Foreign
                                </div>
                            </div>

                            <div className={styles.pricingList}>
                                {pricingData.map((item) => {
                                    const fee = isForeign ? item.intFee : item.indFee;

                                    return (
                                        <div key={item.id} className={`${styles.priceItem} ${item.badge ? styles.priceItemFeatured : ""}`}>
                                            <div className={styles.priceItemTop}>
                                                <div className={styles.priceName}>{item.tier}</div>
                                                {item.badge && <span className={styles.priceBadge}>{item.badge}</span>}
                                            </div>
                                            <div className={styles.priceAmount}>{fee}</div>
                                            <div className={styles.priceCurrency}>
                                                {isForeign ? "International delegate" : "Indian delegate"}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className={styles.priceFooter}>
                                <FaShieldAlt />
                                <span>
                                    Registration fees are displayed in <strong>{isForeign ? "USD" : "INR"}</strong>.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================= FOOTER NOTE ================= */}
                <div className={styles.bottomNote}>
                    <FaShieldAlt />
                    <span>Please ensure that the information entered matches your official identification documents.</span>
                </div>
            </div>
        </div>
    );
};

export default RegistrationPage;